import React, { useState, useEffect } from 'react';
import { ExternalLink, History, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_API_URL;

const pad = (n) => String(n).padStart(2, '0');
// DD-MM-YYYY HH:mm (local time) — same display format used across the app.
const formatStamp = (value) => {
    const d = value ? new Date(value) : null;
    if (!d || isNaN(d.getTime())) return '-';
    return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const isValidLink = (v) => !v || /^https?:\/\/[^\s]+$/i.test(v);

// Shared "Interview Link" table cell for the Admin / Team Lead / Recruiter
// Interviews tabs. Editable inline (saved on Enter / blur), opens the link in a
// new tab, and shows who changed it last plus a full change-history popup.
//
// Props:
//   row       – the candidate row (needs id, task_id, interview_link,
//               interview_link_updated_by, interview_link_updated_at)
//   onSaved   – (candidateId, { interview_link, interview_link_updated_by, interview_link_updated_at }) => void
//   onError   – optional (message) => void; defaults to alert()
const InterviewLinkCell = ({ row, onSaved, onError }) => {
    const saved = row.interview_link || '';
    const [value, setValue] = useState(saved);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [showHistory, setShowHistory] = useState(false);
    const [history, setHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);

    // Keep the box in sync when the row is refreshed from the server.
    useEffect(() => { setValue(row.interview_link || ''); }, [row.interview_link]);

    const fail = (msg) => { setError(msg); (onError || ((m) => window.alert(m)))(msg); };

    const commit = async () => {
        const next = value.trim();
        if (next === saved) { setValue(saved); setError(''); return; }
        if (!isValidLink(next)) { setError('Enter a valid http:// or https:// link'); return; }
        setError('');
        setSaving(true);
        try {
            const res = await fetch(`${API}/api/tasks/${row.task_id}/candidates/${row.id}/stage-remarks`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ interview_link: next })
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setValue(saved);
                fail(data.error || 'Failed to save interview link.');
                return;
            }
            onSaved && onSaved(row.id, {
                interview_link: data.interview_link || null,
                interview_link_updated_by: data.interview_link_updated_by || null,
                interview_link_updated_at: data.interview_link_updated_at || null
            });
        } catch (e) {
            setValue(saved);
            fail('Server connection failed.');
        } finally {
            setSaving(false);
        }
    };

    const openHistory = async () => {
        setShowHistory(true);
        setLoadingHistory(true);
        try {
            const res = await fetch(`${API}/api/tasks/${row.task_id}/candidates/${row.id}/interview-link-history`);
            const data = await res.json().catch(() => []);
            setHistory(res.ok && Array.isArray(data) ? data : []);
        } catch (e) {
            setHistory([]);
        } finally {
            setLoadingHistory(false);
        }
    };

    return (
        <div style={{ width: '150px', minWidth: '150px', maxWidth: '150px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', width: '100%' }}>
                <input
                    type="url"
                    value={value}
                    disabled={saving}
                    placeholder="https://…"
                    aria-label="Interview Link"
                    onChange={(e) => { setValue(e.target.value); setError(''); }}
                    onBlur={commit}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { setValue(saved); setError(''); } }}
                    style={{ flex: 1, minWidth: 0, fontSize: '12px', padding: '5px 6px', borderRadius: '4px', border: `1px solid ${error ? '#dc2626' : '#cbd5e1'}`, background: '#fff', boxSizing: 'border-box' }}
                />
                {saved && isValidLink(saved) && (
                    <a href={saved} target="_blank" rel="noopener noreferrer" title="Open interview link" aria-label="Open interview link" style={{ color: '#2563eb', display: 'flex' }}>
                        <ExternalLink size={14} />
                    </a>
                )}
                <button type="button" onClick={openHistory} title="Interview link history" aria-label="Interview link history"
                    style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', color: '#64748b', display: 'flex' }}>
                    <History size={14} />
                </button>
            </div>
            {error && <div style={{ color: '#dc2626', fontSize: '10px', marginTop: '2px' }}>{error}</div>}
            {row.interview_link_updated_by && !error && (
                <div style={{ color: '#94a3b8', fontSize: '10px', marginTop: '2px' }}>
                    Last changed by {row.interview_link_updated_by} · {formatStamp(row.interview_link_updated_at)}
                </div>
            )}

            {showHistory && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
                    onClick={() => setShowHistory(false)}>
                    <div role="dialog" aria-label="Interview link history" onClick={(e) => e.stopPropagation()}
                        style={{ background: '#fff', borderRadius: '10px', padding: '18px', width: '100%', maxWidth: '560px', maxHeight: '80vh', overflowY: 'auto', fontFamily: 'Inter, sans-serif' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                            <h3 style={{ margin: 0, fontSize: '15px', color: '#0f172a' }}>Interview Link History — {row.name || 'Candidate'}</h3>
                            <button type="button" onClick={() => setShowHistory(false)} aria-label="Close" style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}><X size={16} /></button>
                        </div>
                        {loadingHistory ? (
                            <div style={{ fontSize: '13px', color: '#94a3b8' }}>Loading…</div>
                        ) : history.length === 0 ? (
                            <div style={{ fontSize: '13px', color: '#94a3b8' }}>No changes recorded yet.</div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                {history.map(h => (
                                    <div key={h.id} data-testid="link-history-entry" style={{ border: '1px solid #e2e8f0', borderRadius: '6px', padding: '8px 10px', fontSize: '12px', color: '#334155' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', marginBottom: '4px' }}>
                                            <strong>{h.changed_by}{h.changed_by_role ? ` (${h.changed_by_role})` : ''}</strong>
                                            <span style={{ color: '#64748b' }}>{formatStamp(h.changed_at)}</span>
                                        </div>
                                        <div style={{ wordBreak: 'break-all' }}><span style={{ color: '#64748b' }}>From: </span>{h.old_value || '(empty)'}</div>
                                        <div style={{ wordBreak: 'break-all' }}><span style={{ color: '#64748b' }}>To: </span>{h.new_value || '(removed)'}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default InterviewLinkCell;